/**
 * Properties. The only real CRUD surface in Phase 1.
 *
 * Every row shows its lineage — where the row came from and how it was derived — because
 * "where did this come from?" must be answerable from the UI, not only from the database
 * (CLAUDE.md 13, DATA_MODEL.md 3).
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type Entity, type Market, type Property } from '../api.js';

const AMENITY_LABELS: Record<string, string> = {
  swimming_pool: 'Swimming pool',
  hot_tub: 'Hot tub',
  sauna: 'Sauna',
  golf_simulator: 'Golf simulator',
  game_room: 'Game room',
  theater: 'Theater',
  putting_green: 'Putting green',
  outdoor_entertainment_area: 'Outdoor entertainment',
  large_recreational_space: 'Large rec space',
};

const AMENITIES = Object.keys(AMENITY_LABELS);

export function PropertiesPage(): ReactNode {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<{ properties: Property[] }>('/api/properties'),
  });
  const markets = useQuery({
    queryKey: ['markets'],
    queryFn: () => api.get<{ markets: Market[] }>('/api/markets'),
  });
  const entities = useQuery({
    queryKey: ['entities'],
    queryFn: () => api.get<{ entities: Entity[] }>('/api/entities'),
  });

  const create = useMutation({
    mutationFn: (body: unknown) => api.post<{ property: Property }>('/api/properties', body),
    onSuccess: () => {
      setShowForm(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError
          ? err.killSwitch
            ? `${err.message} (This is the kill switch working as designed.)`
            : err.message
          : 'Something went wrong.',
      );
    },
  });

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    create.mutate({
      name: String(form.get('name') ?? ''),
      marketId: String(form.get('marketId') ?? ''),
      entityId: String(form.get('entityId') ?? ''),
      addressLine1: String(form.get('addressLine1') ?? ''),
      city: String(form.get('city') ?? ''),
      state: String(form.get('state') ?? '').toUpperCase(),
      postalCode: String(form.get('postalCode') ?? ''),
      bedrooms: Number(form.get('bedrooms') ?? 0),
      bathrooms: Number(form.get('bathrooms') ?? 0),
      sleeps: Number(form.get('sleeps') ?? 0),
      amenities: AMENITIES.filter((a) => form.get(`amenity:${a}`) === 'on'),
      status: String(form.get('status') ?? 'active'),
    });
  }

  const rows = properties.data?.properties ?? [];

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Properties</h1>
          <p className="muted">
            {rows.length} {rows.length === 1 ? 'property' : 'properties'}. Sixgen is the system of
            record for this table.
          </p>
        </div>
        <button type="button" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'Add property'}
        </button>
      </div>

      {error && <div className="alert">{error}</div>}

      {showForm && (
        <form className="card form" onSubmit={onSubmit}>
          <div className="grid">
            <label>
              Name
              <input name="name" required maxLength={200} />
            </label>
            <label>
              Market
              <select name="marketId" required defaultValue="">
                <option value="" disabled>
                  Select a market
                </option>
                {(markets.data?.markets ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Entity
              <select name="entityId" required defaultValue="">
                <option value="" disabled>
                  Select an entity
                </option>
                {(entities.data?.entities ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.legal_name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select name="status" defaultValue="active">
                <option value="active">Active</option>
                <option value="onboarding">Onboarding</option>
                <option value="renovating">Renovating</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label className="span-2">
              Address
              <input name="addressLine1" required maxLength={200} />
            </label>
            <label>
              City
              <input name="city" required maxLength={100} />
            </label>
            <label>
              State
              <input name="state" required maxLength={2} minLength={2} placeholder="KY" />
            </label>
            <label>
              Postal code
              <input name="postalCode" required maxLength={20} />
            </label>
            <label>
              Bedrooms
              <input name="bedrooms" type="number" min={0} max={100} defaultValue={4} required />
            </label>
            <label>
              Bathrooms
              <input
                name="bathrooms"
                type="number"
                min={0}
                max={100}
                step={0.5}
                defaultValue={3}
                required
              />
            </label>
            <label>
              Sleeps
              <input name="sleeps" type="number" min={0} max={200} defaultValue={10} required />
            </label>
          </div>

          <fieldset className="amenities">
            <legend>Amenities</legend>
            {AMENITIES.map((a) => (
              <label key={a} className="checkbox">
                <input type="checkbox" name={`amenity:${a}`} />
                {AMENITY_LABELS[a]}
              </label>
            ))}
          </fieldset>

          <button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Create property'}
          </button>
          <p className="muted small">
            Creating a property writes the row, an audit entry, and an event — in one transaction.
            The event appears on the Activity page within seconds.
          </p>
        </form>
      )}

      {properties.isLoading && <p className="muted">Loading…</p>}
      {properties.isError && <div className="alert">Could not load properties.</div>}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Market</th>
                <th>Entity</th>
                <th>Beds / Baths</th>
                <th>Sleeps</th>
                <th>Status</th>
                <th>Bookings</th>
                <th>Lineage</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{p.name}</strong>
                    <div className="muted small">
                      {p.address_line1}, {p.city} {p.state}
                    </div>
                    {p.amenities.length > 0 && (
                      <div className="chips">
                        {p.amenities.map((a) => (
                          <span key={a} className="chip">
                            {AMENITY_LABELS[a] ?? a}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>{p.market_name}</td>
                  <td>{p.entity_name}</td>
                  <td>
                    {p.bedrooms} / {p.bathrooms}
                  </td>
                  <td>{p.sleeps}</td>
                  <td>
                    <span className={`status status-${p.status}`}>{p.status}</span>
                  </td>
                  <td>
                    {/* Through to this property's own bookings. A count here would be a second
                        place reservations are counted, and the Reservations screen already knows
                        how — the link takes the question where the answer lives. */}
                    <Link to={`/reservations?property=${p.id}`}>Reservations</Link>
                  </td>
                  <td className="small muted">
                    <div>source: {p.source_system}</div>
                    <div>derivation: {p.derivation}</div>
                    <div>updated: {new Date(p.updated_at).toLocaleString()}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!properties.isLoading && rows.length === 0 && (
        <div className="card empty">
          <p>No properties yet.</p>
          <p className="muted small">
            Run <code>pnpm seed</code> to load the fictional sample portfolio, or add one above.
          </p>
        </div>
      )}
    </section>
  );
}
